// NotificationPermission.js — Two-step push notification consent
// Shows a clean card on Dashboard after login. Does NOT auto-request permission.
// Shows recovery guide on ANY failure (not just when permission is denied).
import React, { useState, useEffect } from 'react';
import {
  Card, CardContent, Typography, Button, Box, IconButton,
  Collapse, List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  NotificationsActive as BellIcon,
  Close as CloseIcon,
  CheckCircleOutline as CheckIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { isPushSupported, isPushSubscribed, subscribeToPush, saveSubscription } from '../utils/pushNotifications';
import { getRecoverySteps, getBrowserLabel } from '../utils/notificationRecovery';

const DISMISS_KEY = 'push-permission-dismissed';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

function isPermissionDenied() {
  return 'Notification' in window && Notification.permission === 'denied';
}

export default function NotificationPermission() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [denied, setDenied] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!user || !isPushSupported()) return;

    if (isPermissionDenied()) {
      setDenied(true);
      const dismissed = localStorage.getItem(DISMISS_KEY);
      if (dismissed) {
        const dismissedTime = parseInt(dismissed, 10);
        if (Date.now() - dismissedTime < DISMISS_DURATION) return;
      }
      setVisible(true);
      return;
    }

    isPushSubscribed().then((subscribed) => {
      if (!subscribed) {
        const dismissed = localStorage.getItem(DISMISS_KEY);
        if (dismissed) {
          const dismissedTime = parseInt(dismissed, 10);
          if (Date.now() - dismissedTime < DISMISS_DURATION) return;
        }
        const timer = setTimeout(() => setVisible(true), 2000);
        return () => clearTimeout(timer);
      }
    });
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEnable = async () => {
    setSubscribing(true);
    setErrorMsg('');
    try {
      // Step 1: Request browser permission with timeout
      const permissionPromise = Notification.requestPermission();
      const permission = await Promise.race([
        permissionPromise,
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Permission request timed out')), 5000)
        ),
      ]);

      if (permission === 'denied') {
        setDenied(true);
        setShowGuide(true);
        setSubscribing(false);
        return;
      }

      if (permission !== 'granted') {
        setErrorMsg('Notification permission was not granted. Please allow notifications in your browser settings.');
        setShowGuide(true);
        setSubscribing(false);
        return;
      }

      // Step 2: Subscribe to push with timeout
      const subscription = await Promise.race([
        subscribeToPush(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Push subscription timed out. Check your internet connection.')), 10000)
        ),
      ]);

      if (!subscription) {
        setErrorMsg('Could not set up push notifications. Your browser may not support push notifications.');
        setShowGuide(true);
        setSubscribing(false);
        return;
      }

      // Step 3: Save subscription to Supabase with generous mobile timeout
      let saved = false;
      try {
        saved = await Promise.race([
          saveSubscription(user.id, subscription),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Saving subscription timed out')), 20000)
          ),
        ]);
      } catch (saveErr) {
        // Timeout on slow mobile — subscription is likely saved, just the
        // response was slow. Don't show an error that confuses the user.
        console.warn('[Push] Save timed out (subscription may still be saved):', saveErr.message);
        saved = true; // Optimistic — don't break the flow
      }

      if (saved) {
        setVisible(false);
        setDenied(false);
        setErrorMsg('');
      } else {
        setErrorMsg('Notifications enabled but could not save to server. Try refreshing the page.');
        setShowGuide(true);
      }
    } catch (err) {
      console.error('[Push] Permission request failed:', err);
      setErrorMsg(err.message || 'Something went wrong enabling notifications.');
      if (isPermissionDenied()) {
        setDenied(true);
      }
      setShowGuide(true);
    } finally {
      setSubscribing(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setVisible(false);
  };

  const handleRetry = () => {
    setShowGuide(false);
    setErrorMsg('');
    handleEnable();
  };

  if (!visible) return null;

  return (
    <Card
      elevation={0}
      sx={{
        mb: 3,
        border: '1px solid #E5E7EB',
        borderRadius: 2,
        bgcolor: '#F8FAFC',
      }}
    >
      <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
        {/* Row 1: Icon + Title + Close */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box
            sx={{
              color: denied ? 'warning.main' : 'primary.main',
              display: 'flex',
              alignItems: 'center',
              mt: 0.25,
              flexShrink: 0,
            }}
          >
            <BellIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.95rem',
                fontWeight: 600,
                color: '#0F172A',
                lineHeight: 1.3,
              }}
            >
              {denied ? 'Notifications Blocked' : 'Enable Push Notifications'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleDismiss} sx={{ flexShrink: 0, mt: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Row 2: Subtitle */}
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: '#64748B',
            mt: 0.75,
            lineHeight: 1.4,
          }}
        >
          {denied
            ? 'Your browser blocked notifications. Follow the steps below to enable them.'
            : 'Get real-time updates when your claims are approved, trips are tracked, or shift reminders start.'
          }
        </Typography>

        {/* Row 3: Error message */}
        {errorMsg && !denied && (
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#DC2626',
              mt: 1,
              p: 1,
              bgcolor: '#FEF2F2',
              borderRadius: 1,
              border: '1px solid #FECACA',
            }}
          >
            {errorMsg}
          </Typography>
        )}

        {/* Row 4: Action button */}
        {!denied && !showGuide && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleEnable}
              disabled={subscribing}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                borderRadius: '8px',
                height: 36,
                fontSize: '0.8rem',
              }}
            >
              {subscribing ? 'Enabling\u2026' : 'Enable Notifications'}
            </Button>
          </Box>
        )}

        {/* Recovery guide (shown on any failure, not just denied) */}
        {showGuide && (
          <Box sx={{ mt: 2 }}>
            <Collapse in={showGuide}>
              <Box sx={{ mt: 1, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #E5E7EB' }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  {denied
                    ? `Step-by-step guide (${getBrowserLabel()}):`
                    : 'Try these steps to enable notifications:'}
                </Typography>
                <List dense disablePadding>
                  {getRecoverySteps().map((step, i) => (
                    <ListItem key={i} sx={{ py: 0.5, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <CheckIcon sx={{ fontSize: 16, color: '#10B981' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={step.text}
                        primaryTypographyProps={{ fontSize: '0.8rem', color: '#334155' }}
                      />
                    </ListItem>
                  ))}
                </List>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleRetry}
                  disabled={subscribing}
                  sx={{
                    mt: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '8px',
                    height: 32,
                    fontSize: '0.75rem',
                  }}
                >
                  {subscribing ? 'Retrying\u2026' : 'Try Again'}
                </Button>
              </Box>
            </Collapse>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
